import {observer} from 'mobx-react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React from 'react';

import {HeadSEO, useCanonicalUrl} from '@udemy/head-seo';
import {useStores} from '@udemy/store-provider';

import {getLayout} from 'src/components/layout';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {type CategoryData} from 'src/lib/fetchers';
import {RootStore} from 'src/stores/root-store';
import {SubscriberEngagementRecapData, type UserEnrollmentsData} from 'src/types/page-props';

import {PersonalPlanHomePageFeature} from './personal-plan-home-page-feature';

export interface PersonalPlanHomePageProps {
    categories: CategoryData[];
    enableAssessmentsInPersonalPlan: boolean;
    enableCertificationBasedCourseUnitPP: boolean;
    enableLabsInPersonalPlan: boolean;
    enableNewCertificationBasedCourseUnitUIPP: boolean;
    enableNewSkillBasedCourseUnitUIPP: boolean;
    enablePopQuizPP: boolean;
    popQuizV2ExperienceEnabled: boolean;
    enableSkillBasedCourseUnitPP: boolean;
    enableStreaksInPersonalPlan: boolean;
    enableAssessmentsBannerPP: boolean;
    isOccupationCollectionPromptDismissed: boolean;
    topicInterestIds: string[];
    userHasCertificationInterests: boolean;
    userHasSkillInterests: boolean;
    isPaidSubscriber: boolean;
    isValuePlanSubscriber: boolean;
    /** TODO: clean up once lihp_carousel_hover_popup_removal experiment concludes */
    showStateAwareCta: boolean;
    subscriptionStartDate: string | null;
    userHasUBProAccess: boolean;
    showAtRiskFeedbackSurvey: boolean;
    subscriberEngagementRecapData?: SubscriberEngagementRecapData;
    userEnrollments?: UserEnrollmentsData;
}

const PersonalPlanHomePageComponent: React.FC<PersonalPlanHomePageProps> = (props) => {
    const [rootStore] = useStores([RootStore]);
    const router = useRouter();
    const {url: canonicalUrl} = useCanonicalUrl(
        router as {locale: string; asPath: string},
        [],
        META_DEFAULT_BASE_URL,
    );

    return (
        <>
            <HeadSEO
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
                robots={['noindex']}
            />
            <PersonalPlanHomePageFeature {...props} accelerator={rootStore.accelerator} />
        </>
    );
};

const PersonalPlanHomePage = observer(
    PersonalPlanHomePageComponent,
) as React.FC<PersonalPlanHomePageProps> & {
    getLayout: typeof getLayout;
};

PersonalPlanHomePage.getLayout = getLayout;

export {PersonalPlanHomePage};
